import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Trash2, Minus, Plus, ShoppingBag, Loader2, Truck, Store, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useMerchCart } from '../context/MerchCartContext';
import { usePublicAuth } from '../context/PublicAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Single-step checkout (same simple flow as the ticket checkout):
// items + buyer data + delivery + "Pagar $X" in one screen.
export const MerchCartDrawer = () => {
  const { items, count, itemsTotal, updateQty, removeItem, isOpen, closeCart } = useMerchCart();
  const { user } = usePublicAuth() || {};
  const [paying, setPaying] = useState(false);
  const [form, setForm] = useState({ buyer_name: '', buyer_email: '', buyer_phone: '', buyer_dni: '', delivery_method: 'pickup', street: '', city: '', province: '', zip_code: '', notes: '' });

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeCart(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeCart]);

  const allowedMethods = useMemo(() => {
    let pickup = true, shipping = true;
    items.forEach(i => {
      if (i.delivery_options === 'pickup') shipping = false;
      if (i.delivery_options === 'shipping') pickup = false;
    });
    return { pickup, shipping };
  }, [items]);

  // Prefill logged-in user data + valid delivery method when the drawer opens
  useEffect(() => {
    if (!isOpen) return;
    setForm(f => ({
      ...f,
      buyer_name: f.buyer_name || user?.full_name || '',
      buyer_email: f.buyer_email || user?.email || '',
      delivery_method: allowedMethods[f.delivery_method] ? f.delivery_method : (allowedMethods.pickup ? 'pickup' : 'shipping'),
    }));
  }, [isOpen, user, allowedMethods]);

  const maxShipping = useMemo(() => items.reduce((m, i) => Math.max(m, i.shipping_cost || 0), 0), [items]);
  const shippingCost = form.delivery_method === 'shipping' ? maxShipping : 0;
  const total = itemsTotal + shippingCost;

  const pay = async () => {
    if (!form.buyer_name.trim() || !form.buyer_email.includes('@')) return toast.error('Completá tu nombre y email');
    if (form.delivery_method === 'shipping' && (!form.street.trim() || !form.city.trim())) return toast.error('Completá la dirección de envío');
    setPaying(true);
    try {
      const res = await axios.post(`${API}/api/merch/checkout`, {
        items: items.map(i => ({ product_id: i.product_id, variant: i.variant, quantity: i.quantity })),
        buyer_name: form.buyer_name,
        buyer_email: form.buyer_email,
        buyer_phone: form.buyer_phone,
        buyer_dni: form.buyer_dni,
        delivery_method: form.delivery_method,
        shipping_address: form.delivery_method === 'shipping' ? { street: form.street, city: form.city, province: form.province, zip_code: form.zip_code, notes: form.notes } : null,
        user_id: user?.user_id || null,
      });
      window.location.href = res.data.init_point;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pudimos iniciar el pago');
      setPaying(false);
    }
  };

  const inputCls = "w-full bg-[#121212] border border-[#27272A] rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-white/25 focus:border-[#D4AF37] outline-none";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100]" data-testid="merch-cart-drawer">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeCart} />
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.25 }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0B0B0B] border-l border-[#27272A] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#27272A]">
              <h2 className="text-white font-black uppercase tracking-widest flex items-center gap-2 text-sm"><ShoppingBag size={16} className="text-[#D4AF37]" /> Tu compra ({count})</h2>
              <button onClick={closeCart} className="text-white/50 hover:text-white p-1" data-testid="cart-close-btn"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-16">Tu carrito está vacío</p>
              ) : (
                <>
                  {/* Items — compact rows */}
                  <div className="space-y-2">
                    {items.map(i => (
                      <div key={i.key} className="flex items-center gap-3 bg-white/[0.03] border border-white/8 rounded-xl p-2.5" data-testid={`cart-item-${i.key}`}>
                        {i.image ? <img src={i.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-[#121212] flex items-center justify-center flex-shrink-0"><ShoppingBag size={16} className="text-white/20" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-[13px] font-bold truncate">{i.name}{i.variant ? <span className="text-white/40 font-normal"> · {i.variant}</span> : null}</p>
                          <p className="text-[#D4AF37] font-black text-[13px]">${(i.price * i.quantity).toLocaleString('es-AR')}</p>
                        </div>
                        <div className="flex items-center border border-white/15 rounded-full flex-shrink-0">
                          <button onClick={() => updateQty(i.key, i.quantity - 1)} className="w-7 h-7 text-white flex items-center justify-center" data-testid={`cart-qty-minus-${i.key}`}><Minus size={11} /></button>
                          <span className="text-white text-xs font-bold w-5 text-center" data-testid={`cart-qty-value-${i.key}`}>{i.quantity}</span>
                          <button onClick={() => {
                            if (i.max_stock && i.quantity >= i.max_stock) { toast.error(`Solo quedan ${i.max_stock} unidades disponibles`); return; }
                            updateQty(i.key, i.quantity + 1);
                          }} className="w-7 h-7 text-white flex items-center justify-center" data-testid={`cart-qty-plus-${i.key}`}><Plus size={11} /></button>
                        </div>
                        <button onClick={() => removeItem(i.key)} className="text-white/30 hover:text-red-400 p-1 flex-shrink-0" data-testid={`cart-remove-${i.key}`}><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>

                  {/* Buyer data — same minimal fields as the ticket checkout */}
                  <div className="space-y-2.5">
                    <p className="text-white/60 text-[10px] uppercase tracking-[0.25em] font-bold">Tus datos</p>
                    <input className={inputCls} placeholder="Nombre y apellido *" value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} data-testid="checkout-name" />
                    <input className={inputCls} placeholder="Email *" type="email" inputMode="email" autoComplete="email" value={form.buyer_email} onChange={e => setForm({ ...form, buyer_email: e.target.value })} data-testid="checkout-email" />
                    <div className="grid grid-cols-2 gap-2.5">
                      <input className={inputCls} placeholder="Teléfono" inputMode="tel" value={form.buyer_phone} onChange={e => setForm({ ...form, buyer_phone: e.target.value })} data-testid="checkout-phone" />
                      <input className={inputCls} placeholder="DNI" inputMode="numeric" value={form.buyer_dni} onChange={e => setForm({ ...form, buyer_dni: e.target.value })} data-testid="checkout-dni" />
                    </div>
                  </div>

                  {/* Delivery */}
                  <div className="space-y-2.5">
                    <p className="text-white/60 text-[10px] uppercase tracking-[0.25em] font-bold">Entrega</p>
                    <div className="grid grid-cols-2 gap-2.5">
                      <button disabled={!allowedMethods.pickup} onClick={() => setForm({ ...form, delivery_method: 'pickup' })}
                        className={`rounded-xl border p-3 text-left ${form.delivery_method === 'pickup' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10'} ${!allowedMethods.pickup ? 'opacity-30 cursor-not-allowed' : ''}`} data-testid="delivery-pickup">
                        <Store size={15} className="text-[#D4AF37] mb-1" />
                        <p className="text-white text-xs font-bold">Retiro en el evento</p>
                        <p className="text-white/40 text-[10px]">Con código de retiro</p>
                      </button>
                      <button disabled={!allowedMethods.shipping} onClick={() => setForm({ ...form, delivery_method: 'shipping' })}
                        className={`rounded-xl border p-3 text-left ${form.delivery_method === 'shipping' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10'} ${!allowedMethods.shipping ? 'opacity-30 cursor-not-allowed' : ''}`} data-testid="delivery-shipping">
                        <Truck size={15} className="text-[#D4AF37] mb-1" />
                        <p className="text-white text-xs font-bold">Envío a domicilio</p>
                        <p className="text-white/40 text-[10px]">{maxShipping > 0 ? `+$${maxShipping.toLocaleString('es-AR')}` : 'A coordinar'}</p>
                      </button>
                    </div>
                    {form.delivery_method === 'shipping' && (
                      <div className="space-y-2.5">
                        <input className={inputCls} placeholder="Calle y número *" value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} data-testid="checkout-street" />
                        <div className="grid grid-cols-2 gap-2.5">
                          <input className={inputCls} placeholder="Ciudad *" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="checkout-city" />
                          <input className={inputCls} placeholder="Provincia" value={form.province} onChange={e => setForm({ ...form, province: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                          <input className={inputCls} placeholder="Código postal" inputMode="numeric" value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} />
                          <input className={inputCls} placeholder="Notas (piso, depto)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {items.length > 0 && (
              <div className="px-5 pt-3 pb-5 border-t border-[#27272A] space-y-2" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.25rem)' }}>
                <div className="flex justify-between text-xs text-white/50">
                  <span>Subtotal{shippingCost > 0 ? ' + envío' : ''}</span>
                  <span>${itemsTotal.toLocaleString('es-AR')}{shippingCost > 0 ? ` + $${shippingCost.toLocaleString('es-AR')}` : ''}</span>
                </div>
                <button onClick={pay} disabled={paying} className="w-full py-4 bg-[#D4AF37] text-black rounded-full font-black text-sm uppercase tracking-widest hover:bg-[#F2D272] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-pay-btn">
                  {paying ? <Loader2 size={15} className="animate-spin" /> : null}
                  Pagar ${total.toLocaleString('es-AR')}
                </button>
                <p className="text-white/35 text-[10px] text-center flex items-center justify-center gap-1.5" data-testid="cart-total" data-total={total}>
                  <ShieldCheck size={11} /> Pago seguro con Mercado Pago
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
